/**
 * stacked-logo-header — the Home header for headerMode LOGO with a logo set.
 *
 * Expanded: the logo above the business name (and the descriptor line under it)
 * riding just under the device chrome. As the shell's <main> scrolls its first
 * 90px the logo and name slide 1:1 into a horizontal strip on the Telegram
 * chrome row; the descriptor fades out instead of travelling. Position is a pure
 * function of scroll offset: no timers, no thresholds, reversible.
 *
 * Contract: docs/mockups/client-dashboard-redesign/home-logo-header-golden-hour.html
 * and docs/plans/client-webapp-redesign/2026-09-07-logo-lockup-header-spike.md.
 *
 * The clip line under the lockup is a COVER element painting the atmosphere over
 * the content band, not a clip-path/mask on <main>: either of those makes <main>
 * a backdrop root (the cards' glass blur stops seeing the atmosphere) and
 * clip-path also removes the band from hit testing. Per frame this touches only
 * transforms and one clip-path on a static layer.
 */
import { useEffect, useRef, type RefObject } from 'react';
import {
  applyFade,
  applyProgress,
  collapseProgress,
  computeMove,
  type LockupMove,
} from './logoLockupMath';
import styles from './ClientLogoLockupHeader.module.css';

/** Atmosphere kept under the collapsed strip so content never sits flush against it (mockup: ~6px). */
const STRIP_GAP = 8;

interface ClientLogoLockupHeaderProps {
  name: string;
  logoUrl: string;
  descriptor: string | null;
  contentRef: RefObject<HTMLElement>;
}

export function ClientLogoLockupHeader({ name, logoUrl, descriptor, contentRef }: ClientLogoLockupHeaderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const coverRef = useRef<HTMLDivElement>(null);
  const coverPaintRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLSpanElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const nameRef = useRef<HTMLHeadingElement>(null);
  const descriptorRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const targetLogoRef = useRef<HTMLSpanElement>(null);
  const targetNameRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const content = contentRef.current;
    const root = rootRef.current;
    const cover = coverRef.current;
    const coverPaint = coverPaintRef.current;
    const expanded = expandedRef.current;
    const logo = logoRef.current;
    const image = imageRef.current;
    const text = nameRef.current;
    const strip = stripRef.current;
    const targetLogo = targetLogoRef.current;
    const targetName = targetNameRef.current;
    if (!content || !root || !cover || !coverPaint || !expanded || !logo || !image || !text || !strip || !targetLogo || !targetName) return;
    const tag = descriptorRef.current;

    const pairs = [[logo, targetLogo], [text, targetName]] as const;
    let moves: LockupMove[] = [];
    // Height of the lockup below the chrome strip: the band the cover hides at rest.
    let band = 0;
    let fontsReady = false;
    let needsMeasurement = true;
    let disposed = false;
    let frame: number | null = null;

    const measure = () => {
      // Clear applied styles so the rects read the natural layout.
      for (const [from] of pairs) from.style.transform = '';
      if (tag) { tag.style.opacity = ''; tag.style.transform = ''; }
      moves = pairs.map(([from, to]) => computeMove(from.getBoundingClientRect(), to.getBoundingClientRect()));

      const rootRect = root.getBoundingClientRect();
      const stripBottom = strip.getBoundingClientRect().bottom - rootRect.top;
      band = Math.max(0, expanded.getBoundingClientRect().bottom - rootRect.top - stripBottom);
      // The cover spans the band; its paint layer is offset so its pixels line up
      // with the shell atmosphere behind the content.
      cover.style.top = `${stripBottom}px`;
      cover.style.height = `${band}px`;
      coverPaint.style.top = `${-stripBottom}px`;
    };

    const apply = () => {
      frame = null;
      if (needsMeasurement && fontsReady && image.complete) {
        measure();
        needsMeasurement = false;
      }
      const p = collapseProgress(content.scrollTop);
      pairs.forEach(([from], index) => {
        const move = moves[index];
        if (move) from.style.transform = applyProgress(move, p);
      });
      if (tag) {
        const fade = applyFade(p);
        tag.style.opacity = fade.opacity;
        tag.style.transform = fade.transform;
      }
      cover.style.clipPath = p === 0 ? '' : `inset(0 0 ${Math.max(0, band - STRIP_GAP) * p}px 0)`;
    };

    const schedule = () => {
      if (!disposed && frame === null) frame = requestAnimationFrame(apply);
    };
    const remeasure = () => {
      needsMeasurement = true;
      schedule();
    };

    content.addEventListener('scroll', schedule, { passive: true });
    image.addEventListener('load', remeasure);
    // The fixed logo box still has usable geometry if the asset fails to load.
    image.addEventListener('error', remeasure);
    window.addEventListener('resize', remeasure);
    window.visualViewport?.addEventListener('resize', remeasure);
    // Telegram can change its chrome inset separately from a window resize.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(remeasure);
    observer?.observe(strip);

    void (document.fonts?.ready ?? Promise.resolve()).then(() => {
      if (disposed) return;
      fontsReady = true;
      remeasure();
    });
    schedule();

    return () => {
      disposed = true;
      if (frame !== null) cancelAnimationFrame(frame);
      content.removeEventListener('scroll', schedule);
      image.removeEventListener('load', remeasure);
      image.removeEventListener('error', remeasure);
      window.removeEventListener('resize', remeasure);
      window.visualViewport?.removeEventListener('resize', remeasure);
      observer?.disconnect();
      for (const [from] of pairs) from.style.transform = '';
      if (tag) { tag.style.opacity = ''; tag.style.transform = ''; }
      cover.style.clipPath = '';
    };
  }, [contentRef, logoUrl, name, descriptor]);

  return (
    <div ref={rootRef} className={styles.root}>
      {/* Atmosphere-painted cover over the content band under the lockup. */}
      <div ref={coverRef} className={styles.cover} aria-hidden="true">
        <div ref={coverPaintRef} className={styles.coverPaint} />
      </div>
      <div ref={expandedRef} className={styles.expanded}>
        <span ref={logoRef} className={styles.logo}>
          <img ref={imageRef} src={logoUrl} alt={name} />
        </span>
        <h1 ref={nameRef} className={styles.name}>{name}</h1>
        {descriptor ? (
          <div ref={descriptorRef} className={styles.descriptor}>{descriptor}</div>
        ) : null}
      </div>
      {/* Hidden layout only: keep real boxes without a duplicate accessible name. */}
      <div ref={stripRef} className={styles.strip} aria-hidden="true">
        <span ref={targetLogoRef} className={styles.logo} />
        <span ref={targetNameRef} className={styles.name}>{name}</span>
      </div>
    </div>
  );
}
